import React, {useMemo} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../components/content-box/ContentBox";
import Dependencies from "../../../../../utilities/Dependencies";
import Environment from "../../../../../services/interfaces/Environment";
import PageActions from "../../../../base/PageActions";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";

interface PantheonPageProps {
    dependencies: Dependencies,
    env: Environment
}

const PantheonPage = (props: PantheonPageProps) => {

    const page: PageActions = useMemo(() => new PageActions({
        dependencies: props.dependencies,
        env: props.env,
        title: `Casarok Pantheon - Chase the Fox`,
        description: "The pantheon of gods and goddesses in Casarok has separated their influence into various realms, overseen by the four Dragon Deities!",
        keywords: ["casarok", "pantheon", "dragon deities", "gods and goddesses", "fantasy gods"],
        imgSrc: `chase/world/magic/classes/spellcaster.png`,
        url: `world/pantheon`
    }), [props]);

    return page.generateMetaContent(
        <>
            <ContentBox header="The Casarok Pantheon">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <p>
                        Casarok has a large pantheon of gods and goddesses. They are all beings of pure mana given a
                        physical body. They manifest their own forms, and their choice of physical appearance is based
                        on their personality and ideals of life and balance. Benevolent gods often appear white or
                        shining while those of darker nature favor darker colors and tougher appearances. Each god or
                        goddess has chosen a different physical form to take. These origins make the gods and goddesses
                        essentially immortal due to the fact that they are made up of pure life energy and they can
                        prevent their bodies from ever aging.
                    </p>
                    <p>
                        Due to being made of
                        pure <NavLink className="world-link" to="/world/magic">mana</NavLink>, they have an
                        extensive reserve of magical power. Rather than letting their abilities run rampant, they have
                        each agreed to narrow their focus to a designated realm of magical influence. Each god or
                        goddess is responsible for a different aspect of Casarok or the universe as a whole. If they
                        extend beyond their realm of power, they are punished by the arbiter of the gods. Another reason
                        for their magical designations is their immortality. The gods and goddesses are incapable of
                        destroying each other with their own magical abilities; however, their physical forms come with
                        the flaw that mortal beings can slay them (given that a mortal can overcome their massive
                        magical power.) The rationale for the existence of this flaw has yet to be determined.
                    </p>
                    <h3 className="headline">Dragon Deities</h3>
                    <p>
                        The strongest and most highly respected of the gods and goddesses. They have the greatest
                        responsibilities among the entire pantheon and act as the council that overhears the disputes of
                        the other deities.
                    </p>
                    <ul>
                        <li><strong>Tianlong</strong>: The eastern dragon god of the stars and heavens</li>
                        <li><strong>Ragnarok</strong>: The western dragon god of death and destruction</li>
                        <li><strong>Galacta</strong>: The void dragon goddess of space and the universe</li>
                        <li><strong>Tempatrius</strong>: The sand dragon god of time</li>
                    </ul>
                    <h3 className="headline">Major Gods</h3>
                    <p>
                        Major and elemental gods who have had a large impact on the creation and events of the world.
                    </p>
                    <ul>
                        <li><strong>Mother</strong>: The kitsune goddess of life and creation</li>
                        <li><strong>Ptahlemy</strong>: The roc god of science, technology, and knowledge</li>
                        <li><strong>Hazado</strong>: The tanuki god of magic</li>
                        <li><strong>Blaze</strong>: The salamander elemental goddess of fire</li>
                        <li><strong>Jotun</strong>: The yeti elemental god of frost</li>
                        <li><strong>Barbaru</strong>: The raiju elemental god of lightning</li>
                        <li><strong>Domathair</strong>: The unicorn elemental goddess of nature</li>
                        <li><strong>Terranis</strong>: The behemoth elemental god of earth</li>
                        <li><strong>Shen Ling</strong>: The kirin elemental goddess of spirit</li>
                        <li><strong>Sapphiron</strong>: The merfolk elemental god of water</li>
                        <li><strong>Ingal</strong>: The kamaitachi elemental goddess of wind</li>
                    </ul>
                    <h3 className="headline">Minor Gods</h3>
                    <p>
                        The remainder of the gods and goddesses play lesser, yet still important, roles in the world.
                    </p>
                    <ul>
                        <li><strong>Stratos</strong>: The garuda god of the sky, clouds, and cloudlands</li>
                        <li><strong>Hieracine</strong>: The sphinx goddess of the desert</li>
                        <li><strong>Kine</strong>: The owlbeast god and protector of the forests</li>
                        <li><strong>Phoenicia</strong>: The phoenix goddess of the sun and daytime</li>
                        <li><strong>Mohiga</strong>: The werewolf goddess of the moon and nighttime</li>
                        <li><strong>Lionheart</strong>: The gryphon god of courage, bravery, and valor</li>
                        <li><strong>Aerisa</strong>: The tengu goddess of hearth and home</li>
                        <li><strong>Kentauros</strong>: The centaur god of war and hunting</li>
                        <li><strong>Bastille</strong>: The nekomata goddess of health and protection</li>
                        <li><strong>Caduceus</strong>: The pegasus god of medicine and healing</li>
                        <li><strong>Lyre</strong>: The satyr god of music, poerty, storytelling, and the arts</li>
                        <li><strong>Yamuna</strong>: The naga goddess of chaos, fear, and natural disasters</li>
                        <li><strong>Quetzen</strong>: The coatl god of priests and tradesmen</li>
                        <li><strong>Cobalt</strong>: The kobold goddess of gems and precious metals</li>
                        <li><strong>Volper</strong>: The jackalope god of luck and fortune</li>
                        <li><strong>Hypnos</strong>: The rainbow serpent god of sleep and dreams</li>
                        <li><strong>Aimi</strong>: The chakat goddess of love, beauty, marriage, and fertility</li>
                        <li><strong>Aster</strong>: The minotaur god of masculinity and strength</li>
                        <li><strong>Chrystalia</strong>: The basilisk goddess of trickery and deceit</li>
                        <li><strong>Voki</strong>: The white stag god of the astral plane</li>
                        <li><strong>Maita</strong>: The cait sith goddess of masks, costumes, and transformation</li>
                        <li><strong>Ferni</strong>: The cu sith god of the underworld</li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default PantheonPage;